/**
 * Pure helpers + shared types for the overlay variables model used by
 * `OverlayPropertyPanel`, the resolver hook, and round-trip tests. Kept in a
 * non-`.tsx` sibling so fast refresh stays clean on the component file.
 */

export type VariableType = "string" | "number" | "color" | "boolean" | "image";

export type VariableSource =
  | { kind: "static" }
  | { kind: "binding"; path: string };

export interface VariableEntry {
  name: string;
  type: VariableType;
  defaultValue: unknown;
  source?: VariableSource;
  goalTarget?: number;
}

interface VariableMeta {
  type?: VariableType;
  source?: VariableSource;
  goalTarget?: number;
}

export const decodeVariables = (
  raw: Record<string, unknown> | undefined,
): VariableEntry[] => {
  if (!raw) return [];
  return Object.entries(raw).map(([name, value]) => {
    if (
      value &&
      typeof value === "object" &&
      "__meta" in (value as Record<string, unknown>)
    ) {
      const meta = (value as { __meta: VariableMeta; value: unknown }).__meta;
      const inner = (value as { __meta: unknown; value: unknown }).value;
      const entry: VariableEntry = {
        name,
        type: meta.type ?? "string",
        defaultValue: inner,
      };
      if (meta.source !== undefined) {
        entry.source = meta.source;
      }
      if (meta.goalTarget !== undefined) {
        entry.goalTarget = meta.goalTarget;
      }
      return entry;
    }
    let type: VariableType = "string";
    if (typeof value === "number") type = "number";
    else if (typeof value === "boolean") type = "boolean";
    return { name, type, defaultValue: value };
  });
};

export const encodeVariables = (
  entries: VariableEntry[],
): Record<string, unknown> => {
  const out: Record<string, unknown> = {};
  for (const entry of entries) {
    if (!entry.name) continue;
    const hasBinding =
      entry.source !== undefined && entry.source.kind === "binding";
    const hasGoalTarget = entry.goalTarget !== undefined;
    const needsMeta =
      entry.type === "color" ||
      entry.type === "image" ||
      hasBinding ||
      hasGoalTarget;
    if (needsMeta) {
      const meta: VariableMeta = { type: entry.type };
      if (hasBinding) {
        meta.source = entry.source;
      }
      if (hasGoalTarget) {
        meta.goalTarget = entry.goalTarget;
      }
      out[entry.name] = {
        __meta: meta,
        value: entry.defaultValue,
      };
    } else {
      out[entry.name] = entry.defaultValue;
    }
  }
  return out;
};
